import { useCallback, useContext, useEffect, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Row, Select, message } from "antd";
import { GET_ITEM_CATEGORIES_API } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import CustomDatePicker from "common/CustomDatePicker";
import { StoreRequisitionContext } from "context/StoreRequisitionContext/StoreRequisitionContextProvider";
import moment from "moment";
import ItemAddDrawer from "./ItemAddDrawer";
import StyleAddDrawer from "./StyleAddDrawer";
import StyleItemCard from "./StyleItemCard";
import {
  getItemCategoryFilterBySrType,
  storeRequisitionMovementTypes,
  storeRequisitionSrTypes,
} from "./constants";

const { Option } = Select;
const { TextArea } = Input;

const StoreRequisitionForm = ({ form, viewMode = false }) => {
  const {
    buyerList,
    factoryList,
    styleDetails,
    getBuyerList,
    getFactoryList,
    getStyleListByBuyerOrFactory,
    addStyleDetail,
    updateStyleDetail,
    removeStyleDetail,
    addItemToStyle,
    updateItemInStyle,
    removeItemFromStyle,
    resetStyleDetails,
  } = useContext(StoreRequisitionContext);

  const [styleDrawerOpen, setStyleDrawerOpen] = useState(false);
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemDrawerStyleKey, setItemDrawerStyleKey] = useState(null);
  const [categoryList, setCategoryList] = useState([]);

  const buyerIdWatch = Form.useWatch("buyer_id", form);
  const factoryIdWatch = Form.useWatch("sub_contract_factory_id", form);
  const srTypeWatch = Form.useWatch("sr_type", form);
  const itemCategoryIdsWatch = Form.useWatch("item_category_ids", form);

  const itemDrawerStyle = styleDetails.find((s) => s.key === itemDrawerStyleKey);

  const loadCategoryList = useCallback(async (srType) => {
    if (!srType) {
      setCategoryList([]);
      return;
    }

    const filterParams = getItemCategoryFilterBySrType(srType);
    const response = await getData(GET_ITEM_CATEGORIES_API, false, {
      per_page: 500,
      ...filterParams,
    });

    if (response?.data?.code === 200) {
      let categories = response?.data?.data?.data || response?.data?.data || [];

      if (srType === "carton") {
        categories = categories.filter((category) =>
          String(category?.name || "")
            .toLowerCase()
            .includes("carton"),
        );
      }

      setCategoryList(categories);
      return;
    }

    setCategoryList([]);
  }, []);

  useEffect(() => {
    getBuyerList();
    getFactoryList();
  }, [getBuyerList, getFactoryList]);

  useEffect(() => {
    if (viewMode) return;
    loadCategoryList(srTypeWatch);
  }, [srTypeWatch, loadCategoryList, viewMode]);

  const handleSrTypeChange = () => {
    if (viewMode) return;
    form.setFieldsValue({ item_category_ids: [] });
    resetStyleDetails();
    setItemDrawerStyleKey(null);
    setEditingItem(null);
    setEditingStyle(null);
    setItemDrawerOpen(false);
  };

  useEffect(() => {
    if (viewMode) return;

    if (buyerIdWatch) {
      getStyleListByBuyerOrFactory({
        buyer_id: buyerIdWatch,
        sub_contract_factory_id: null,
      });
      return;
    }

    if (factoryIdWatch) {
      getStyleListByBuyerOrFactory({
        buyer_id: null,
        sub_contract_factory_id: factoryIdWatch,
      });
      return;
    }

    getStyleListByBuyerOrFactory();
  }, [buyerIdWatch, factoryIdWatch, getStyleListByBuyerOrFactory, viewMode]);

  const handleBuyerChange = (value) => {
    if (value) {
      form.setFieldsValue({ sub_contract_factory_id: undefined });
    }
    if (!viewMode) {
      resetStyleDetails();
      setItemDrawerStyleKey(null);
      setEditingItem(null);
      setEditingStyle(null);
    }
  };

  const handleFactoryChange = (value) => {
    if (value) {
      form.setFieldsValue({ buyer_id: undefined });
    }
    if (!viewMode) {
      resetStyleDetails();
      setItemDrawerStyleKey(null);
      setEditingItem(null);
      setEditingStyle(null);
    }
  };

  const handleSaveStyle = (styleRow) => {
    if (editingStyle) {
      updateStyleDetail(editingStyle.key, {
        ...styleRow,
        items: editingStyle.items || [],
      });
    } else {
      addStyleDetail(styleRow);
    }
    setEditingStyle(null);
  };

  const handleSaveItem = (itemRow) => {
    if (!itemDrawerStyleKey) return;

    if (editingItem) {
      updateItemInStyle(itemDrawerStyleKey, editingItem.key, itemRow);
    } else {
      addItemToStyle(itemDrawerStyleKey, itemRow);
    }
    setEditingItem(null);
  };

  const handleDeleteStyle = (styleKey) => {
    removeStyleDetail(styleKey);
    if (itemDrawerStyleKey === styleKey) {
      setItemDrawerStyleKey(null);
      setItemDrawerOpen(false);
      setEditingItem(null);
    }
  };

  const handleOpenAddItem = (styleKey) => {
    if (!srTypeWatch) {
      message.error("Please select SR Type first");
      return;
    }

    setItemDrawerStyleKey(styleKey);
    setEditingItem(null);
    setItemDrawerOpen(true);
  };

  const handleOpenEditItem = (styleKey, item) => {
    setItemDrawerStyleKey(styleKey);
    setEditingItem(item);
    setItemDrawerOpen(true);
  };

  const handleDeleteItem = (styleKey, itemKey) => {
    removeItemFromStyle(styleKey, itemKey);
    if (editingItem?.key === itemKey && itemDrawerStyleKey === styleKey) {
      setEditingItem(null);
      setItemDrawerOpen(false);
    }
  };

  return (
    <>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item label="Buyer" name="buyer_id">
            <Select
              showSearch
              allowClear
              placeholder="Select buyer"
              optionFilterProp="children"
              disabled={viewMode || !!factoryIdWatch}
              onChange={handleBuyerChange}
              size="small"
            >
              {buyerList.map((buyer) => (
                <Option key={buyer.id} value={buyer.id}>
                  {buyer.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Factory" name="sub_contract_factory_id">
            <Select
              showSearch
              allowClear
              placeholder="Select factory"
              optionFilterProp="children"
              disabled={viewMode || !!buyerIdWatch}
              onChange={handleFactoryChange}
              size="small"
            >
              {factoryList.map((factory) => (
                <Option key={factory.id} value={factory.id}>
                  {factory.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label="Date"
            name="entry_date"
            initialValue={viewMode ? undefined : moment()}
            rules={[{ required: true, message: "Date is required" }]}
          >
            <CustomDatePicker
              style={{ width: "100%" }}
              disabled={viewMode}
              size="small"
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Requisition No" name="requisition_no" rules={[{ required: true, message: "Requisition No is required" }]}>
            <Input placeholder="" disabled={viewMode} size="small" />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label="Movement Type"
            name="movement_type"
            rules={[{ required: true, message: "Movement type is required" }]}
          >
            <Select
              showSearch
              placeholder="Select movement type"
              optionFilterProp="children"
              disabled={viewMode}
              size="small"
            >
              {storeRequisitionMovementTypes.map((type) => (
                <Option key={type.id} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="SR Type"
            name="sr_type"
            rules={[{ required: true, message: "SR Type is required" }]}
          >
            <Select
              showSearch
              placeholder="Select SR type"
              optionFilterProp="children"
              disabled={viewMode}
              size="small"
              onChange={handleSrTypeChange}
            >
              {storeRequisitionSrTypes.map((type) => (
                <Option key={type.id} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Category" name="item_category_ids">
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select category"
              optionFilterProp="children"
              disabled={viewMode || !srTypeWatch}
              size="small"
            >
              {categoryList.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {viewMode && (
          <Col span={4}>
            <Form.Item label="Status" name="status">
              <Input disabled size="small" />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item label="Note" name="note">
            <TextArea rows={2} disabled={viewMode} size="small" />
          </Form.Item>
        </Col>
      </Row>

      {!viewMode && (
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            disabled={!buyerIdWatch && !factoryIdWatch}
            onClick={() => {
              setEditingStyle(null);
              setStyleDrawerOpen(true);
            }}
          >
            Add Style
          </Button>
        </div>
      )}

      {styleDetails.map((style) => (
        <StyleItemCard
          key={style.key}
          style={style}
          viewMode={viewMode}
          onEditStyle={(styleRow) => {
            setEditingStyle(styleRow);
            setStyleDrawerOpen(true);
          }}
          onDeleteStyle={handleDeleteStyle}
          onAddItem={handleOpenAddItem}
          onEditItem={handleOpenEditItem}
          onDeleteItem={handleDeleteItem}
        />
      ))}

      <StyleAddDrawer
        open={styleDrawerOpen}
        onClose={() => {
          setStyleDrawerOpen(false);
          setEditingStyle(null);
        }}
        onSave={handleSaveStyle}
        editData={editingStyle}
      />

      <ItemAddDrawer
        open={itemDrawerOpen}
        onClose={() => {
          setItemDrawerOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSaveItem}
        styleId={itemDrawerStyle?.style_id}
        subStyleMap={itemDrawerStyle?.sub_style_map || []}
        editData={editingItem}
        srType={srTypeWatch}
        itemCategoryIds={itemCategoryIdsWatch}
      />
    </>
  );
};

export default StoreRequisitionForm;
